import { create } from 'zustand';
import { AxiosError } from 'axios';
import { i18n } from '../i18n';
import { getTenantSettings, updateTenantSettings } from '../api/tenantSettings';
import type { TenantDto, UpdateTenantSettingsRequest } from '../types/tenantSettings';
import { useToastStore } from './useToastStore';

interface TenantSettingsState {
  tenant: TenantDto | null;
  loading: boolean;
  saving: boolean;
  error: string | null;

  fetchTenant: () => Promise<void>;
  updateTenant: (data: UpdateTenantSettingsRequest) => Promise<TenantDto | null>;
  reset: () => void;
}

const t = (key: string) => i18n.t(key, { ns: 'tenantSettings' }) as string;

function extractServerError(error: unknown): string | null {
  if (error instanceof AxiosError) {
    const body = error.response?.data as { error?: string; message?: string } | undefined;
    return body?.error || body?.message || null;
  }
  return null;
}

export const useTenantSettingsStore = create<TenantSettingsState>((set) => ({
  tenant: null,
  loading: false,
  saving: false,
  error: null,

  fetchTenant: async () => {
    set({ loading: true, error: null });
    try {
      const data = await getTenantSettings();
      set({ tenant: data, loading: false });
    } catch (error) {
      console.error('[TenantSettingsStore] fetch failed', error);
      set({
        error: extractServerError(error) || t('errors.fetchFailed'),
        loading: false,
      });
    }
  },

  updateTenant: async (data) => {
    set({ saving: true });
    try {
      const updated = await updateTenantSettings(data);
      set({ tenant: updated, saving: false });
      useToastStore.getState().addToast(t('messages.updated'), 'success');
      return updated;
    } catch (error) {
      console.error('[TenantSettingsStore] update failed', error);
      set({ saving: false });
      const message = extractServerError(error) || t('errors.saveFailed');
      useToastStore.getState().addToast(message, 'error');
      return null;
    }
  },

  reset: () => set({ tenant: null, loading: false, saving: false, error: null }),
}));
